import { useRef, memo } from 'react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface FilterOption {
    id: string;
    label: string;
    icon?: React.ReactNode;
}

interface FilterBarProps {
    options: FilterOption[];
    activeFilter: string;
    onSelect: (id: string) => void;
}

export const FilterBar = memo(function FilterBar({ options, activeFilter, onSelect }: FilterBarProps) {
    const { selectionChanged } = useHapticFeedback();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    return (
        <div className="relative w-full mb-6 group flex items-center gap-2 no-scrollbar">
            {/* Scrollable Filters */}
            <div
                className="overflow-x-auto no-scrollbar flex items-center gap-2 snap-x px-4"
                ref={scrollContainerRef}
            >
                {options.map((option) => {
                    const isActive = activeFilter === option.id && option.id !== 'filter';
                    return (
                        <motion.button
                            key={option.id}
                            whileTap={{ 
                                scale: 0.97,
                                transition: { duration: 0.1 }
                            }}
                            onClick={() => {
                                selectionChanged();
                                onSelect(option.id);
                            }}
                            className={cn(
                                "relative flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap font-medium text-[14px] snap-start transition-colors duration-200 select-none overflow-hidden",
                                isActive
                                    ? "text-white font-extrabold tracking-wide"
                                    : "bg-tg-secondary/70 text-tg-hint hover:bg-tg-text/10 hover:text-tg-text"
                            )}
                        >
                            {/* SHARED LIQUID GLASS CAPSULE */}
                            {isActive && (
                                <motion.div
                                    layoutId="sharedLiquidGlassCapsule"
                                    transition={{
                                        type: "spring",
                                        stiffness: 450,
                                        damping: 35
                                    }}
                                    className="absolute inset-0 rounded-xl pointer-events-none z-0 overflow-hidden"
                                    style={{
                                        transform: 'translateZ(0)',
                                        backdropFilter: 'blur(12px) saturate(160%)',
                                        WebkitBackdropFilter: 'blur(12px) saturate(160%)',
                                        background: 'rgba(255, 255, 255, 0.04)',
                                        border: '1px solid rgba(255, 255, 255, 0.12)',
                                        boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.15), 0 4px 14px rgba(0, 0, 0, 0.35)'
                                    }}
                                />
                            )}

                            <span className="relative z-10 flex items-center gap-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
                                {option.icon && (
                                    <span className={cn("w-4 h-4 transition-colors", option.id === 'filter' ? "text-blue-400" : "text-white/90")}>
                                        {option.icon}
                                    </span>
                                )}
                                <span className={cn(isActive ? "text-white font-extrabold" : "")}>
                                    {option.label}
                                </span>
                            </span>
                        </motion.button>
                    );
                })}
            </div>
        </div>
    );
});



